import type { GuideUnit } from '@/components/app/layout/columns-rows-guides-dialog'
import type { PreferencesValues } from '@/components/app/layout/preferences-dialog'
import type { UnitOfMeasure } from '@/lib/units/unit-conversion'
import { createRequiredContext } from './create-required-context'

export interface ColumnsRowsSettings {
  columns: number
  rows: number
  colGutter: number
  rowGutter: number
}

/**
 * Editor view, snap, guide and preference settings, with their setters. Changes rarely (a menu
 * toggle, a dialog save), so it's safe for many components to read.
 */
export interface EditorSettings {
  gridMode: 'off' | 'full' | 'half'
  snapToGrid: boolean
  showGuides: boolean
  showSmartGuides: boolean
  snapToGuides: boolean
  /** Smart-guide / guide snap distance in points (from Preferences). */
  snapGuideDistance: number
  /** Arrow-key nudge distance in points (from Preferences). */
  nudgeDistancePt: number
  /** View setting: print-bleed tint outside trim ("Show Bleed Bar"). */
  showBleed: boolean
  showTextLinking: boolean
  showStickyNotes: boolean
  /** `null` until book settings load; rulers draw no ticks until a unit is known. */
  rulerUnit: GuideUnit | null
  unitOfMeasure: UnitOfMeasure
  columns: number
  rows: number
  colGutter: number
  rowGutter: number
  defaultGuttersLinked: boolean
  isGuidesDialogOpen: boolean
  preferencesValues: PreferencesValues
  setGuidesDialogOpen: (open: boolean) => void
  setColumnsRows: (settings: ColumnsRowsSettings) => void
  setPreferences: (next: PreferencesValues) => void
  toggleFullPica: (checked: boolean) => void
  toggleHalfPica: (checked: boolean) => void
  toggleSnapToGrid: (checked: boolean) => void
  toggleShowGuides: (checked: boolean) => void
  toggleShowSmartGuides: (checked: boolean) => void
  toggleSnapToGuides: (checked: boolean) => void
  toggleShowBleedBar: (checked: boolean) => void
  toggleShowTextLinking: (checked: boolean) => void
  toggleShowStickyNotes: (checked: boolean) => void
}

const editorSettings = createRequiredContext<EditorSettings>('EditorSettings')

export const EditorSettingsContext = editorSettings.Context
export const EditorSettingsProvider = editorSettings.Provider
export const useEditorSettings = editorSettings.useRequiredContext

/** The document unit (Object/Photo panel field units). Shorthand for `useEditorSettings().unitOfMeasure`. */
export function useUnitOfMeasure(): UnitOfMeasure {
  return useEditorSettings().unitOfMeasure
}
